// src/screens/marketingCRM/dashboard.jsx - Updated with tooltips and left-aligned tables
import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import ScreenHeader from "../../components/ScreenHeader";
import Pagination from "../../components/Pagination";
import {
  LogOut, PlusCircle, List, Calendar, UserPlus, Users,
  Eye, Edit, Trash2, FileText, FileSpreadsheet,
  X, ArrowLeft, ChevronRight
} from "lucide-react";
import "./dashboard.css";

const PAGE_SIZE = 10;

// Action Button Component with Tooltip (only for icon buttons)
const ActionIconButton = ({ icon, label, onClick, colorClass }) => (
  <button className={`action-btn ${colorClass}`} onClick={onClick} style={{ position: 'relative' }}>
    {icon}
    <span className="tooltip-text">{label}</span>
  </button>
);

export default function MarketingCRMDashboard({ user, onLogout }) {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("add-marketing-data");
  const [showLogoutModal, setShowLogoutModal] = useState(false);
  const { fromRoute } = useSelector(state => state.navigation || { fromRoute: null });
  const cameFromMDDashboard = fromRoute === "/managing-director";

  // Extra fields states
  const [marketingExtraFields, setMarketingExtraFields] = useState([]);
  const [followupExtraFields, setFollowupExtraFields] = useState([]);
  const [supplierExtraFields, setSupplierExtraFields] = useState([]);
  const [customerExtraFields, setCustomerExtraFields] = useState([]);

  // Validation attempt flags
  const [marketingSubmitAttempted, setMarketingSubmitAttempted] = useState(false);
  const [followupSubmitAttempted, setFollowupSubmitAttempted] = useState(false);
  const [supplierSubmitAttempted, setSupplierSubmitAttempted] = useState(false);
  const [customerSubmitAttempted, setCustomerSubmitAttempted] = useState(false);

  // Pagination states
  const [marketingPage, setMarketingPage] = useState(1);
  const [followupPage, setFollowupPage] = useState(1);
  const [supplierPage, setSupplierPage] = useState(1);
  const [customerPage, setCustomerPage] = useState(1);

  // Data states
  const [marketingData, setMarketingData] = useState([
    { id: 1, companyName: "ABC Industries", pocName: "John Doe", location: "Chennai", designation: "Manager", mobile: "9876543210", email: "john@abc.com", whatsappNumber: "9876543210", employee: "Rajesh", address: "Chennai Main Road", createdAt: new Date().toISOString() }
  ]);
  const [nextFollowups, setNextFollowups] = useState([
    { id: 1, companyName: "XYZ Corp", pocName: "Jane Smith", location: "Bangalore", designation: "Director", mobile: "9876543211", email: "jane@xyz.com", whatsappNumber: "9876543211", address: "Bangalore Main Road", followupDate: "2024-01-20", notes: "Follow up for quotation", status: "Pending" }
  ]);
  const [suppliers, setSuppliers] = useState([
    { id: 1, companyName: "SK Suppliers", pocName: "SK", username: "sk@suppliers.com", password: "123456", address: "Coimbatore", pan: "ABCDE1234F", bankDetails: "ICICI Bank", gst: "33BBBBB0000B2Z", phone: "9876543211", supplierCode: "SUP-123" }
  ]);
  const [customers, setCustomers] = useState([
    { id: 1, companyName: "ABC Industries", pocName: "John Doe", username: "john@abc.com", password: "123456", address: "Chennai", pan: "ABCDE1234G", bankDetails: "HDFC Bank", gst: "22AAAAA0000A1Z", phone: "9876543210", vendorCode: "CUST-123" }
  ]);

  // Form states
  const [marketingForm, setMarketingForm] = useState({ companyName: '', address: '', location: '', pocName: '', designation: '', mobile: '', email: '', whatsappNumber: '', employee: '' });
  const [followupForm, setFollowupForm] = useState({ companyName: '', address: '', location: '', pocName: '', designation: '', mobile: '', email: '', whatsappNumber: '', followupDate: '', notes: '' });
  const [supplierForm, setSupplierForm] = useState({ pocName: '', companyName: '', username: '', password: '', address: '', pan: '', bankDetails: '', gst: '', phone: '', supplierCode: 'SUP-' + Date.now() });
  const [customerForm, setCustomerForm] = useState({ pocName: '', companyName: '', username: '', password: '', address: '', pan: '', bankDetails: '', gst: '', phone: '', vendorCode: 'CUST-' + Date.now() });

  // Navigation handlers
  const handleLogoutClick = () => setShowLogoutModal(true);
  const handleConfirmLogout = () => { setShowLogoutModal(false); onLogout(); navigate('/login'); };
  const handleCancelLogout = () => setShowLogoutModal(false);
  const handleBackToMD = () => navigate("/managing-director");

  // Validation functions
  const validateMarketingForm = () => {
    const e = {};
    if (!marketingForm.companyName.trim()) e.companyName = 'Company name is required';
    if (!marketingForm.location.trim()) e.location = 'Location is required';
    if (!marketingForm.pocName.trim()) e.pocName = 'Point of contact name is required';
    if (!marketingForm.mobile.trim()) e.mobile = 'Mobile number is required';
    if (!marketingForm.email.trim()) e.email = 'Email is required';
    if (!marketingForm.address.trim()) e.address = 'Address is required';
    if (!marketingForm.employee.trim()) e.employee = 'Employee name is required';
    return e;
  };
  const validateFollowupForm = () => {
    const e = {};
    if (!followupForm.companyName.trim()) e.companyName = 'Company name is required';
    if (!followupForm.pocName.trim()) e.pocName = 'Point of contact is required';
    if (!followupForm.mobile.trim()) e.mobile = 'Mobile is required';
    if (!followupForm.followupDate) e.followupDate = 'Followup date is required';
    return e;
  };
  const validateSupplierForm = () => {
    const e = {};
    if (!supplierForm.pocName.trim()) e.pocName = 'POC Name is required';
    if (!supplierForm.companyName.trim()) e.companyName = 'Company Name is required';
    if (!supplierForm.username.trim()) e.username = 'Username is required';
    if (!supplierForm.password.trim()) e.password = 'Password is required';
    if (!supplierForm.address.trim()) e.address = 'Address is required';
    return e;
  };
  const validateCustomerForm = () => {
    const e = {};
    if (!customerForm.pocName.trim()) e.pocName = 'POC Name is required';
    if (!customerForm.companyName.trim()) e.companyName = 'Company Name is required';
    if (!customerForm.username.trim()) e.username = 'Username is required';
    if (!customerForm.password.trim()) e.password = 'Password is required';
    if (!customerForm.address.trim()) e.address = 'Address is required';
    return e;
  };

  // Error getters
  const getMErr = (f) => { if (!marketingSubmitAttempted) return null; return validateMarketingForm()[f]; };
  const getFErr = (f) => { if (!followupSubmitAttempted) return null; return validateFollowupForm()[f]; };
  const getSuppErr = (f) => { if (!supplierSubmitAttempted) return null; return validateSupplierForm()[f]; };
  const getCErr = (f) => { if (!customerSubmitAttempted) return null; return validateCustomerForm()[f]; };

  // Submit handlers
  const handleAddMarketingData = () => {
    setMarketingSubmitAttempted(true);
    if (Object.keys(validateMarketingForm()).length > 0) return;
    setMarketingData([...marketingData, { id: Date.now(), ...marketingForm, extraFields: marketingExtraFields, createdAt: new Date().toISOString() }]);
    setMarketingForm({ companyName: '', address: '', location: '', pocName: '', designation: '', mobile: '', email: '', whatsappNumber: '', employee: '' });
    setMarketingExtraFields([]);
    setMarketingSubmitAttempted(false);
    alert('Marketing data added successfully!');
  };
  const handleAddFollowup = () => {
    setFollowupSubmitAttempted(true);
    if (Object.keys(validateFollowupForm()).length > 0) return;
    setNextFollowups([...nextFollowups, { id: Date.now(), ...followupForm, extraFields: followupExtraFields, status: 'Pending', createdAt: new Date().toISOString() }]);
    setFollowupForm({ companyName: '', address: '', location: '', pocName: '', designation: '', mobile: '', email: '', whatsappNumber: '', followupDate: '', notes: '' });
    setFollowupExtraFields([]);
    setFollowupSubmitAttempted(false);
    alert('Followup added successfully!');
  };
  const handleCreateSupplier = () => {
    setSupplierSubmitAttempted(true);
    if (Object.keys(validateSupplierForm()).length > 0) return;
    setSuppliers([...suppliers, { id: Date.now(), ...supplierForm, extraFields: supplierExtraFields, createdAt: new Date().toISOString() }]);
    setSupplierForm({ pocName: '', companyName: '', username: '', password: '', address: '', pan: '', bankDetails: '', gst: '', phone: '', supplierCode: 'SUP-' + Date.now() });
    setSupplierExtraFields([]);
    setSupplierSubmitAttempted(false);
    alert('Supplier created successfully!');
  };
  const handleCreateCustomer = () => {
    setCustomerSubmitAttempted(true);
    if (Object.keys(validateCustomerForm()).length > 0) return;
    setCustomers([...customers, { id: Date.now(), ...customerForm, extraFields: customerExtraFields, createdAt: new Date().toISOString() }]);
    setCustomerForm({ pocName: '', companyName: '', username: '', password: '', address: '', pan: '', bankDetails: '', gst: '', phone: '', vendorCode: 'CUST-' + Date.now() });
    setCustomerExtraFields([]);
    setCustomerSubmitAttempted(false);
    alert('Customer created successfully!');
  };

  const handleMoveToNPD = (followup) => {
    alert(`Moving ${followup.companyName} to NPD department`);
    setNextFollowups(nextFollowups.filter(f => f.id !== followup.id));
  };

  // Extra field handlers
  const addExtraField = (type) => {
    if (type === 'marketing') setMarketingExtraFields([...marketingExtraFields, { name: '', value: '' }]);
    if (type === 'followup') setFollowupExtraFields([...followupExtraFields, { name: '', value: '' }]);
    if (type === 'supplier') setSupplierExtraFields([...supplierExtraFields, { name: '', value: '' }]);
    if (type === 'customer') setCustomerExtraFields([...customerExtraFields, { name: '', value: '' }]);
  };
  const updateExtraField = (type, index, field, value) => {
    if (type === 'marketing') { const u = [...marketingExtraFields]; u[index][field] = value; setMarketingExtraFields(u); }
    if (type === 'followup') { const u = [...followupExtraFields]; u[index][field] = value; setFollowupExtraFields(u); }
    if (type === 'supplier') { const u = [...supplierExtraFields]; u[index][field] = value; setSupplierExtraFields(u); }
    if (type === 'customer') { const u = [...customerExtraFields]; u[index][field] = value; setCustomerExtraFields(u); }
  };
  const removeExtraField = (type, index) => {
    if (type === 'marketing') setMarketingExtraFields(marketingExtraFields.filter((_, i) => i !== index));
    if (type === 'followup') setFollowupExtraFields(followupExtraFields.filter((_, i) => i !== index));
    if (type === 'supplier') setSupplierExtraFields(supplierExtraFields.filter((_, i) => i !== index));
    if (type === 'customer') setCustomerExtraFields(customerExtraFields.filter((_, i) => i !== index));
  };

  // Filter function
  const filteredData = (data, fields) => {
    return data;
  };

  // Pagination helper
  const paginate = (data, page) => data.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const formatValue = (value) => {
    if (value === undefined || value === null || value === '') return 'N/A';
    return value;
  };

  // Sidebar menu items
  const tabItems = [
    { id: "add-marketing-data", label: "Add Marketing Data", icon: <PlusCircle size={18} /> },
    { id: "list-marketing-data", label: "List Marketing Data", icon: <List size={18} /> },
    { id: "list-next-followup", label: "List of Next Follow Up", icon: <Calendar size={18} /> },
    { id: "create-supplier", label: "Create Supplier", icon: <UserPlus size={18} /> },
    { id: "create-customer", label: "Create Customer", icon: <Users size={18} /> }
  ];

  // ==================== RENDER FUNCTIONS ====================

  const renderAddMarketingData = () => (
    <div>
      <div className="section-header">
        <h2 className="section-title">Add Marketing Data</h2>
      </div>

      <div className="marketing-form" style={{ background: '#f8fafc', padding: '20px', borderRadius: '12px', marginBottom: '24px', border: '1px solid #e2e8f0' }}>
        <div className="form-grid">
          <div className="form-group">
            <label>Company name <span className="required-star">*</span></label>
            <input type="text" value={marketingForm.companyName} onChange={e => setMarketingForm({...marketingForm, companyName: e.target.value})} className={getMErr('companyName') ? 'error' : ''} />
            {getMErr('companyName') && <span className="error-text">{getMErr('companyName')}</span>}
          </div>
          <div className="form-group">
            <label>Location <span className="required-star">*</span></label>
            <input type="text" value={marketingForm.location} onChange={e => setMarketingForm({...marketingForm, location: e.target.value})} className={getMErr('location') ? 'error' : ''} />
            {getMErr('location') && <span className="error-text">{getMErr('location')}</span>}
          </div>
          <div className="form-group">
            <label>Point of Contact Name <span className="required-star">*</span></label>
            <input type="text" value={marketingForm.pocName} onChange={e => setMarketingForm({...marketingForm, pocName: e.target.value})} className={getMErr('pocName') ? 'error' : ''} />
            {getMErr('pocName') && <span className="error-text">{getMErr('pocName')}</span>}
          </div>
          <div className="form-group">
            <label>Designation <span className="optional-text">(Optional)</span></label>
            <input type="text" value={marketingForm.designation} onChange={e => setMarketingForm({...marketingForm, designation: e.target.value})} />
          </div>
          <div className="form-group">
            <label>Mobile <span className="required-star">*</span></label>
            <input type="tel" value={marketingForm.mobile} onChange={e => setMarketingForm({...marketingForm, mobile: e.target.value})} className={getMErr('mobile') ? 'error' : ''} />
            {getMErr('mobile') && <span className="error-text">{getMErr('mobile')}</span>}
          </div>
          <div className="form-group">
            <label>Email <span className="required-star">*</span></label>
            <input type="email" value={marketingForm.email} onChange={e => setMarketingForm({...marketingForm, email: e.target.value})} className={getMErr('email') ? 'error' : ''} />
            {getMErr('email') && <span className="error-text">{getMErr('email')}</span>}
          </div>
          <div className="form-group">
            <label>WhatsApp Number <span className="optional-text">(Optional)</span></label>
            <input type="tel" value={marketingForm.whatsappNumber} onChange={e => setMarketingForm({...marketingForm, whatsappNumber: e.target.value})} />
          </div>
          <div className="form-group">
            <label>Employee <span className="required-star">*</span></label>
            <input type="text" value={marketingForm.employee} onChange={e => setMarketingForm({...marketingForm, employee: e.target.value})} placeholder="Assigned employee" className={getMErr('employee') ? 'error' : ''} />
            {getMErr('employee') && <span className="error-text">{getMErr('employee')}</span>}
          </div>
        </div>

        {/* Long text area - Address placed before extra fields */}
        <div className="form-group full-width" style={{ marginBottom: '16px' }}>
          <label>Address <span className="required-star">*</span></label>
          <textarea rows="3" value={marketingForm.address} onChange={e => setMarketingForm({...marketingForm, address: e.target.value})} className={getMErr('address') ? 'error' : ''} />
          {getMErr('address') && <span className="error-text">{getMErr('address')}</span>}
        </div>

        <div style={{ margin: '16px 0', padding: '16px', background: '#F9FAFB', borderRadius: '8px' }}>
          <label style={{ fontWeight: 600, fontSize: '13px', color: '#475569' }}>Add Extra Fields <span className="optional-text">(Optional)</span></label>
          {marketingExtraFields.map((field, index) => (
            <div key={index} style={{ display: 'flex', gap: '12px', marginTop: '10px', alignItems: 'center' }}>
              <input type="text" placeholder="Field name" value={field.name} onChange={e => updateExtraField('marketing', index, 'name', e.target.value)} style={{ flex: 1, padding: '8px 12px', border: '1px solid #e2e8f0', borderRadius: '6px', fontSize: '14px' }} />
              <input type="text" placeholder="Field value" value={field.value} onChange={e => updateExtraField('marketing', index, 'value', e.target.value)} style={{ flex: 1, padding: '8px 12px', border: '1px solid #e2e8f0', borderRadius: '6px', fontSize: '14px' }} />
              <button onClick={() => removeExtraField('marketing', index)} style={{ padding: '6px', background: '#FEE2E2', border: 'none', borderRadius: '6px', cursor: 'pointer', color: '#DC2626' }}><X size={16} /></button>
            </div>
          ))}
          <button className="add-field-btn" onClick={() => addExtraField('marketing')} style={{ marginTop: '10px' }}><PlusCircle size={14} /> Add field</button>
        </div>

        <div className="form-actions">
          <button className="btn-cancel" onClick={() => { setMarketingForm({ companyName: '', address: '', location: '', pocName: '', designation: '', mobile: '', email: '', whatsappNumber: '', employee: '' }); setMarketingExtraFields([]); setMarketingSubmitAttempted(false); }}>Cancel</button>
          <button className="btn-submit" onClick={handleAddMarketingData}>Submit</button>
        </div>
      </div>

      {marketingData.length > 0 && (
        <div className="list-section">
          <h3>Recent Marketing Data</h3>
          <div className="data-cards">
            {marketingData.slice(-4).reverse().map(data => (
              <div key={data.id} className="data-card">
                <div className="data-card-header">
                  <strong>{formatValue(data.companyName)}</strong>
                  <button className="view-btn-small" onClick={() => alert(JSON.stringify(data, null, 2))}>View</button>
                </div>
                <div className="data-card-details">
                  <p><span>POC:</span> {formatValue(data.pocName)}</p>
                  <p><span>Location:</span> {formatValue(data.location)}</p>
                  <p><span>Mobile:</span> {formatValue(data.mobile)}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );

  const renderListMarketingData = () => {
    const filtered = filteredData(marketingData, ['companyName', 'pocName', 'location', 'email']);
    const totalPages = Math.ceil(filtered.length / PAGE_SIZE);
    const pageData = paginate(filtered, marketingPage);
    return (
      <div>
        <div className="section-header">
          <h2 className="section-title">Marketing Data</h2>
          <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
            <button className="export-btn pdf" onClick={() => alert('Exporting to PDF...')}><FileText size={16} /> PDF</button>
            <button className="export-btn excel" onClick={() => alert('Exporting to Excel...')}><FileSpreadsheet size={16} /> Excel</button>
          </div>
        </div>
        {marketingData.length === 0 ? (
          <div className="empty-state"><Users size={48} color="#CBD5E1" /><p>No marketing data available</p></div>
        ) : (
          <>
            <div className="data-table-container">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Company Name</th><th>Address</th><th>Location</th><th>POC Name</th><th>Designation</th>
                    <th>Mobile</th><th>Email</th><th>WhatsApp</th><th>Employee</th><th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {pageData.map(data => (
                    <tr key={data.id}>
                      <td className="highlight">{formatValue(data.companyName)}</td>
                      <td>{formatValue(data.address)}</td>
                      <td>{formatValue(data.location)}</td>
                      <td>{formatValue(data.pocName)}</td>
                      <td>{formatValue(data.designation)}</td>
                      <td>{formatValue(data.mobile)}</td>
                      <td>{formatValue(data.email)}</td>
                      <td>{formatValue(data.whatsappNumber)}</td>
                      <td>{formatValue(data.employee)}</td>
                      <td className="action-buttons">
                        <ActionIconButton icon={<Eye size={16} />} label="View" onClick={() => alert(JSON.stringify(data, null, 2))} colorClass="view" />
                        <ActionIconButton icon={<Edit size={16} />} label="Edit" onClick={() => alert('Edit feature coming soon')} colorClass="edit" />
                        <ActionIconButton icon={<Trash2 size={16} />} label="Delete" onClick={() => { if (window.confirm('Delete?')) setMarketingData(marketingData.filter(d => d.id !== data.id)); }} colorClass="delete" />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <Pagination currentPage={marketingPage} totalPages={totalPages} onPageChange={setMarketingPage} pageSize={PAGE_SIZE} totalItems={filtered.length} />
          </>
        )}
      </div>
    );
  };

  const renderListNextFollowup = () => {
    const filtered = filteredData(nextFollowups, ['companyName', 'pocName', 'location']);
    const totalPages = Math.ceil(filtered.length / PAGE_SIZE);
    const pageData = paginate(filtered, followupPage);
    return (
      <div>
        <div className="section-header">
          <h2 className="section-title">List of Next Followup</h2>
        </div>

        <div className="marketing-form" style={{ background: '#f8fafc', padding: '20px', borderRadius: '12px', marginBottom: '24px', border: '1px solid #e2e8f0' }}>
          <div className="form-grid">
            <div className="form-group">
              <label>Company name <span className="required-star">*</span></label>
              <input type="text" value={followupForm.companyName} onChange={e => setFollowupForm({...followupForm, companyName: e.target.value})} className={getFErr('companyName') ? 'error' : ''} />
              {getFErr('companyName') && <span className="error-text">{getFErr('companyName')}</span>}
            </div>
            <div className="form-group">
              <label>Location <span className="optional-text">(Optional)</span></label>
              <input type="text" value={followupForm.location} onChange={e => setFollowupForm({...followupForm, location: e.target.value})} />
            </div>
            <div className="form-group">
              <label>Point of Contact Name <span className="required-star">*</span></label>
              <input type="text" value={followupForm.pocName} onChange={e => setFollowupForm({...followupForm, pocName: e.target.value})} className={getFErr('pocName') ? 'error' : ''} />
              {getFErr('pocName') && <span className="error-text">{getFErr('pocName')}</span>}
            </div>
            <div className="form-group">
              <label>Designation <span className="optional-text">(Optional)</span></label>
              <input type="text" value={followupForm.designation} onChange={e => setFollowupForm({...followupForm, designation: e.target.value})} />
            </div>
            <div className="form-group">
              <label>Mobile <span className="required-star">*</span></label>
              <input type="tel" value={followupForm.mobile} onChange={e => setFollowupForm({...followupForm, mobile: e.target.value})} className={getFErr('mobile') ? 'error' : ''} />
              {getFErr('mobile') && <span className="error-text">{getFErr('mobile')}</span>}
            </div>
            <div className="form-group">
              <label>Email <span className="optional-text">(Optional)</span></label>
              <input type="email" value={followupForm.email} onChange={e => setFollowupForm({...followupForm, email: e.target.value})} />
            </div>
            <div className="form-group">
              <label>WhatsApp Number <span className="optional-text">(Optional)</span></label>
              <input type="tel" value={followupForm.whatsappNumber} onChange={e => setFollowupForm({...followupForm, whatsappNumber: e.target.value})} />
            </div>
            <div className="form-group">
              <label>Followup Date <span className="required-star">*</span></label>
              <input type="date" value={followupForm.followupDate} onChange={e => setFollowupForm({...followupForm, followupDate: e.target.value})} className={getFErr('followupDate') ? 'error' : ''} />
              {getFErr('followupDate') && <span className="error-text">{getFErr('followupDate')}</span>}
            </div>
          </div>

          {/* Long text areas - Address and Notes placed before extra fields */}
          <div className="form-group full-width" style={{ marginBottom: '16px' }}>
            <label>Address <span className="optional-text">(Optional)</span></label>
            <textarea rows="3" value={followupForm.address} onChange={e => setFollowupForm({...followupForm, address: e.target.value})} />
          </div>

          <div className="form-group full-width" style={{ marginBottom: '16px' }}>
            <label>Notes <span className="optional-text">(Optional)</span></label>
            <textarea rows="2" value={followupForm.notes} onChange={e => setFollowupForm({...followupForm, notes: e.target.value})} />
          </div>

          <div style={{ margin: '16px 0', padding: '16px', background: '#F9FAFB', borderRadius: '8px' }}>
            <label style={{ fontWeight: 600, fontSize: '13px', color: '#475569' }}>Add Extra Fields <span className="optional-text">(Optional)</span></label>
            {followupExtraFields.map((field, index) => (
              <div key={index} style={{ display: 'flex', gap: '12px', marginTop: '10px', alignItems: 'center' }}>
                <input type="text" placeholder="Field name" value={field.name} onChange={e => updateExtraField('followup', index, 'name', e.target.value)} style={{ flex: 1, padding: '8px 12px', border: '1px solid #e2e8f0', borderRadius: '6px', fontSize: '14px' }} />
                <input type="text" placeholder="Field value" value={field.value} onChange={e => updateExtraField('followup', index, 'value', e.target.value)} style={{ flex: 1, padding: '8px 12px', border: '1px solid #e2e8f0', borderRadius: '6px', fontSize: '14px' }} />
                <button onClick={() => removeExtraField('followup', index)} style={{ padding: '6px', background: '#FEE2E2', border: 'none', borderRadius: '6px', cursor: 'pointer', color: '#DC2626' }}><X size={16} /></button>
              </div>
            ))}
            <button className="add-field-btn" onClick={() => addExtraField('followup')} style={{ marginTop: '10px' }}><PlusCircle size={14} /> Add field</button>
          </div>

          <div className="form-actions">
            <button className="btn-cancel" onClick={() => { setFollowupForm({ companyName: '', address: '', location: '', pocName: '', designation: '', mobile: '', email: '', whatsappNumber: '', followupDate: '', notes: '' }); setFollowupExtraFields([]); setFollowupSubmitAttempted(false); }}>Cancel</button>
            <button className="btn-submit" onClick={handleAddFollowup}>Add Followup</button>
          </div>
        </div>

        {nextFollowups.length === 0 ? (
          <div className="empty-state"><Calendar size={48} color="#CBD5E1" /><p>No followups scheduled</p></div>
        ) : (
          <>
            <div className="data-table-container">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Company Name</th><th>Address</th><th>Location</th><th>POC Name</th><th>Designation</th>
                    <th>Mobile</th><th>Email</th><th>WhatsApp</th><th>Followup Date</th><th>Status</th><th>Notes</th><th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {pageData.map(followup => (
                    <tr key={followup.id}>
                      <td className="highlight">{formatValue(followup.companyName)}</td>
                      <td>{formatValue(followup.address)}</td>
                      <td>{formatValue(followup.location)}</td>
                      <td>{formatValue(followup.pocName)}</td>
                      <td>{formatValue(followup.designation)}</td>
                      <td>{formatValue(followup.mobile)}</td>
                      <td>{formatValue(followup.email)}</td>
                      <td>{formatValue(followup.whatsappNumber)}</td>
                      <td>{formatValue(followup.followupDate)}</td>
                      <td><span className={`status-badge ${followup.status === 'Pending' ? 'pending' : 'approved'}`}>{formatValue(followup.status)}</span></td>
                      <td>{formatValue(followup.notes)}</td>
                      <td className="action-buttons">
                        <button className="move-to-npd-btn" onClick={() => handleMoveToNPD(followup)}><ChevronRight size={14} /> NPD</button>
                        <ActionIconButton icon={<Edit size={16} />} label="Edit" onClick={() => alert('Edit feature coming soon')} colorClass="edit" />
                        <ActionIconButton icon={<Trash2 size={16} />} label="Delete" onClick={() => { if (window.confirm('Delete?')) setNextFollowups(nextFollowups.filter(f => f.id !== followup.id)); }} colorClass="delete" />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <Pagination currentPage={followupPage} totalPages={totalPages} onPageChange={setFollowupPage} pageSize={PAGE_SIZE} totalItems={filtered.length} />
          </>
        )}
      </div>
    );
  };

  const renderCreateSupplier = () => {
    const filtered = filteredData(suppliers, ['companyName', 'pocName', 'username']);
    const totalPages = Math.ceil(filtered.length / PAGE_SIZE);
    const pageData = paginate(filtered, supplierPage);
    return (
      <div>
        <div className="section-header">
          <h2 className="section-title">Supplier Management</h2>
        </div>

        <div className="marketing-form" style={{ background: '#f8fafc', padding: '20px', borderRadius: '12px', marginBottom: '24px', border: '1px solid #e2e8f0' }}>
          <div className="form-grid">
            <div className="form-group"><label>POC Name <span className="required-star">*</span></label><input type="text" value={supplierForm.pocName} onChange={e => setSupplierForm({...supplierForm, pocName: e.target.value})} className={getSuppErr('pocName') ? 'error' : ''} />{getSuppErr('pocName') && <span className="error-text">{getSuppErr('pocName')}</span>}</div>
            <div className="form-group"><label>Company Name <span className="required-star">*</span></label><input type="text" value={supplierForm.companyName} onChange={e => setSupplierForm({...supplierForm, companyName: e.target.value})} className={getSuppErr('companyName') ? 'error' : ''} />{getSuppErr('companyName') && <span className="error-text">{getSuppErr('companyName')}</span>}</div>
            <div className="form-group"><label>Username <span className="required-star">*</span></label><input type="text" value={supplierForm.username} onChange={e => setSupplierForm({...supplierForm, username: e.target.value})} className={getSuppErr('username') ? 'error' : ''} />{getSuppErr('username') && <span className="error-text">{getSuppErr('username')}</span>}</div>
            <div className="form-group"><label>Password <span className="required-star">*</span></label><input type="password" value={supplierForm.password} onChange={e => setSupplierForm({...supplierForm, password: e.target.value})} className={getSuppErr('password') ? 'error' : ''} />{getSuppErr('password') && <span className="error-text">{getSuppErr('password')}</span>}</div>
            <div className="form-group"><label>PAN <span className="optional-text">(Optional)</span></label><input type="text" value={supplierForm.pan} onChange={e => setSupplierForm({...supplierForm, pan: e.target.value})} /></div>
            <div className="form-group"><label>Bank Details <span className="optional-text">(Optional)</span></label><input type="text" value={supplierForm.bankDetails} onChange={e => setSupplierForm({...supplierForm, bankDetails: e.target.value})} /></div>
            <div className="form-group"><label>GST <span className="optional-text">(Optional)</span></label><input type="text" value={supplierForm.gst} onChange={e => setSupplierForm({...supplierForm, gst: e.target.value})} /></div>
            <div className="form-group"><label>Phone <span className="optional-text">(Optional)</span></label><input type="tel" value={supplierForm.phone} onChange={e => setSupplierForm({...supplierForm, phone: e.target.value})} /></div>
            <div className="form-group"><label>Supplier Code</label><input type="text" value={supplierForm.supplierCode} disabled className="auto-field" /><small>Auto-generated</small></div>
          </div>

          {/* Long text area - Address placed before extra fields */}
          <div className="form-group full-width" style={{ marginBottom: '16px' }}>
            <label>Address <span className="required-star">*</span></label>
            <textarea rows="3" value={supplierForm.address} onChange={e => setSupplierForm({...supplierForm, address: e.target.value})} className={getSuppErr('address') ? 'error' : ''} />
            {getSuppErr('address') && <span className="error-text">{getSuppErr('address')}</span>}
          </div>

          <div style={{ margin: '16px 0', padding: '16px', background: '#F9FAFB', borderRadius: '8px' }}>
            <label style={{ fontWeight: 600, fontSize: '13px', color: '#475569' }}>Add Excess Fields <span className="optional-text">(Optional)</span></label>
            {supplierExtraFields.map((field, index) => (
              <div key={index} style={{ display: 'flex', gap: '12px', marginTop: '10px', alignItems: 'center' }}>
                <input type="text" placeholder="Field name" value={field.name} onChange={e => updateExtraField('supplier', index, 'name', e.target.value)} style={{ flex: 1, padding: '8px 12px', border: '1px solid #e2e8f0', borderRadius: '6px', fontSize: '14px' }} />
                <input type="text" placeholder="Field value" value={field.value} onChange={e => updateExtraField('supplier', index, 'value', e.target.value)} style={{ flex: 1, padding: '8px 12px', border: '1px solid #e2e8f0', borderRadius: '6px', fontSize: '14px' }} />
                <button onClick={() => removeExtraField('supplier', index)} style={{ padding: '6px', background: '#FEE2E2', border: 'none', borderRadius: '6px', cursor: 'pointer', color: '#DC2626' }}><X size={16} /></button>
              </div>
            ))}
            <button className="add-field-btn" onClick={() => addExtraField('supplier')} style={{ marginTop: '10px' }}><PlusCircle size={14} /> Add Excess fields</button>
          </div>

          <div className="form-actions">
            <button className="btn-cancel" onClick={() => { setSupplierForm({ pocName: '', companyName: '', username: '', password: '', address: '', pan: '', bankDetails: '', gst: '', phone: '', supplierCode: 'SUP-' + Date.now() }); setSupplierExtraFields([]); setSupplierSubmitAttempted(false); }}>Cancel</button>
            <button className="btn-submit" onClick={handleCreateSupplier}>Submit</button>
          </div>
        </div>

        <div className="list-section">
          <h3>List Suppliers ({filtered.length})</h3>
          {suppliers.length === 0 ? <div className="empty-state"><p>No suppliers yet.</p></div> : (
            <>
              <div className="data-table-container">
                <table className="data-table">
                  <thead>
                    <tr><th>POC Name</th><th>Company Name</th><th>Username</th><th>Password</th><th>Address</th><th>PAN</th><th>Bank Details</th><th>GST</th><th>Phone</th><th>Supplier Code</th><th>Actions</th></tr>
                  </thead>
                  <tbody>
                    {pageData.map(supplier => (
                      <tr key={supplier.id}>
                        <td>{formatValue(supplier.pocName)}</td>
                        <td className="highlight">{formatValue(supplier.companyName)}</td>
                        <td>{formatValue(supplier.username)}</td>
                        <td>{'••••••'}</td>
                        <td>{formatValue(supplier.address)}</td>
                        <td>{formatValue(supplier.pan)}</td>
                        <td>{formatValue(supplier.bankDetails)}</td>
                        <td>{formatValue(supplier.gst)}</td>
                        <td>{formatValue(supplier.phone)}</td>
                        <td>{formatValue(supplier.supplierCode)}</td>
                        <td className="action-buttons">
                          <ActionIconButton icon={<Eye size={16} />} label="View" onClick={() => alert(JSON.stringify(supplier, null, 2))} colorClass="view" />
                          <ActionIconButton icon={<Edit size={16} />} label="Edit" onClick={() => alert('Edit feature coming soon')} colorClass="edit" />
                          <ActionIconButton icon={<Trash2 size={16} />} label="Delete" onClick={() => { if (window.confirm('Delete?')) setSuppliers(suppliers.filter(s => s.id !== supplier.id)); }} colorClass="delete" />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <Pagination currentPage={supplierPage} totalPages={totalPages} onPageChange={setSupplierPage} pageSize={PAGE_SIZE} totalItems={filtered.length} />
            </>
          )}
        </div>
      </div>
    );
  };

  const renderCreateCustomer = () => {
    const filtered = filteredData(customers, ['companyName', 'pocName', 'username']);
    const totalPages = Math.ceil(filtered.length / PAGE_SIZE);
    const pageData = paginate(filtered, customerPage);
    return (
      <div>
        <div className="section-header">
          <h2 className="section-title">Customer Management</h2>
        </div>

        <div className="marketing-form" style={{ background: '#f8fafc', padding: '20px', borderRadius: '12px', marginBottom: '24px', border: '1px solid #e2e8f0' }}>
          <div className="form-grid">
            <div className="form-group"><label>POC Name <span className="required-star">*</span></label><input type="text" value={customerForm.pocName} onChange={e => setCustomerForm({...customerForm, pocName: e.target.value})} className={getCErr('pocName') ? 'error' : ''} />{getCErr('pocName') && <span className="error-text">{getCErr('pocName')}</span>}</div>
            <div className="form-group"><label>Company Name <span className="required-star">*</span></label><input type="text" value={customerForm.companyName} onChange={e => setCustomerForm({...customerForm, companyName: e.target.value})} className={getCErr('companyName') ? 'error' : ''} />{getCErr('companyName') && <span className="error-text">{getCErr('companyName')}</span>}</div>
            <div className="form-group"><label>Username <span className="required-star">*</span></label><input type="text" value={customerForm.username} onChange={e => setCustomerForm({...customerForm, username: e.target.value})} className={getCErr('username') ? 'error' : ''} />{getCErr('username') && <span className="error-text">{getCErr('username')}</span>}</div>
            <div className="form-group"><label>Password <span className="required-star">*</span></label><input type="password" value={customerForm.password} onChange={e => setCustomerForm({...customerForm, password: e.target.value})} className={getCErr('password') ? 'error' : ''} />{getCErr('password') && <span className="error-text">{getCErr('password')}</span>}</div>
            <div className="form-group"><label>PAN <span className="optional-text">(Optional)</span></label><input type="text" value={customerForm.pan} onChange={e => setCustomerForm({...customerForm, pan: e.target.value})} /></div>
            <div className="form-group"><label>Bank Details <span className="optional-text">(Optional)</span></label><input type="text" value={customerForm.bankDetails} onChange={e => setCustomerForm({...customerForm, bankDetails: e.target.value})} /></div>
            <div className="form-group"><label>GST <span className="optional-text">(Optional)</span></label><input type="text" value={customerForm.gst} onChange={e => setCustomerForm({...customerForm, gst: e.target.value})} /></div>
            <div className="form-group"><label>Phone <span className="optional-text">(Optional)</span></label><input type="tel" value={customerForm.phone} onChange={e => setCustomerForm({...customerForm, phone: e.target.value})} /></div>
            <div className="form-group"><label>Vendor Code</label><input type="text" value={customerForm.vendorCode} disabled className="auto-field" /><small>Auto-generated</small></div>
          </div>

          {/* Long text area - Address placed before extra fields */}
          <div className="form-group full-width" style={{ marginBottom: '16px' }}>
            <label>Address <span className="required-star">*</span></label>
            <textarea rows="3" value={customerForm.address} onChange={e => setCustomerForm({...customerForm, address: e.target.value})} className={getCErr('address') ? 'error' : ''} />
            {getCErr('address') && <span className="error-text">{getCErr('address')}</span>}
          </div>

          <div style={{ margin: '16px 0', padding: '16px', background: '#F9FAFB', borderRadius: '8px' }}>
            <label style={{ fontWeight: 600, fontSize: '13px', color: '#475569' }}>Add Excess Fields <span className="optional-text">(Optional)</span></label>
            {customerExtraFields.map((field, index) => (
              <div key={index} style={{ display: 'flex', gap: '12px', marginTop: '10px', alignItems: 'center' }}>
                <input type="text" placeholder="Field name" value={field.name} onChange={e => updateExtraField('customer', index, 'name', e.target.value)} style={{ flex: 1, padding: '8px 12px', border: '1px solid #e2e8f0', borderRadius: '6px', fontSize: '14px' }} />
                <input type="text" placeholder="Field value" value={field.value} onChange={e => updateExtraField('customer', index, 'value', e.target.value)} style={{ flex: 1, padding: '8px 12px', border: '1px solid #e2e8f0', borderRadius: '6px', fontSize: '14px' }} />
                <button onClick={() => removeExtraField('customer', index)} style={{ padding: '6px', background: '#FEE2E2', border: 'none', borderRadius: '6px', cursor: 'pointer', color: '#DC2626' }}><X size={16} /></button>
              </div>
            ))}
            <button className="add-field-btn" onClick={() => addExtraField('customer')} style={{ marginTop: '10px' }}><PlusCircle size={14} /> Add Excess fields</button>
          </div>

          <div className="form-actions">
            <button className="btn-cancel" onClick={() => { setCustomerForm({ pocName: '', companyName: '', username: '', password: '', address: '', pan: '', bankDetails: '', gst: '', phone: '', vendorCode: 'CUST-' + Date.now() }); setCustomerExtraFields([]); setCustomerSubmitAttempted(false); }}>Cancel</button>
            <button className="btn-submit" onClick={handleCreateCustomer}>Submit</button>
          </div>
        </div>

        <div className="list-section">
          <h3>List Customers ({filtered.length})</h3>
          {customers.length === 0 ? <div className="empty-state"><p>No customers yet.</p></div> : (
            <>
              <div className="data-table-container">
                <table className="data-table">
                  <thead>
                    <tr><th>POC Name</th><th>Company Name</th><th>Username</th><th>Password</th><th>Address</th><th>PAN</th><th>Bank Details</th><th>GST</th><th>Phone</th><th>Vendor Code</th><th>Actions</th></tr>
                  </thead>
                  <tbody>
                    {pageData.map(customer => (
                      <tr key={customer.id}>
                        <td>{formatValue(customer.pocName)}</td>
                        <td className="highlight">{formatValue(customer.companyName)}</td>
                        <td>{formatValue(customer.username)}</td>
                        <td>{'••••••'}</td>
                        <td>{formatValue(customer.address)}</td>
                        <td>{formatValue(customer.pan)}</td>
                        <td>{formatValue(customer.bankDetails)}</td>
                        <td>{formatValue(customer.gst)}</td>
                        <td>{formatValue(customer.phone)}</td>
                        <td>{formatValue(customer.vendorCode)}</td>
                        <td className="action-buttons">
                          <ActionIconButton icon={<Eye size={16} />} label="View" onClick={() => alert(JSON.stringify(customer, null, 2))} colorClass="view" />
                          <ActionIconButton icon={<Edit size={16} />} label="Edit" onClick={() => alert('Edit feature coming soon')} colorClass="edit" />
                          <ActionIconButton icon={<Trash2 size={16} />} label="Delete" onClick={() => { if (window.confirm('Delete?')) setCustomers(customers.filter(c => c.id !== customer.id)); }} colorClass="delete" />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <Pagination currentPage={customerPage} totalPages={totalPages} onPageChange={setCustomerPage} pageSize={PAGE_SIZE} totalItems={filtered.length} />
            </>
          )}
        </div>
      </div>
    );
  };

  const renderContent = () => {
    switch(activeTab) {
      case "add-marketing-data": return renderAddMarketingData();
      case "list-marketing-data": return renderListMarketingData();
      case "list-next-followup": return renderListNextFollowup();
      case "create-supplier": return renderCreateSupplier();
      case "create-customer": return renderCreateCustomer();
      default: return renderAddMarketingData();
    }
  };

  return (
    <div className="marketing-crm-dashboard-container">
      <div className="marketing-crm-sidebar">
        <div className="marketing-crm-logo-container">
          <div className="marketing-crm-logo-icon">
            <img src="/rahulerp.jpg" alt="Logo" className="marketing-crm-logo-image" onError={e => { e.target.onerror = null; e.target.src = 'https://via.placeholder.com/48x48?text=RAT'; }} />
          </div>
          <div>
            <h2 className="marketing-crm-company-name">Ragul Auto Tech</h2>
            <p className="marketing-crm-company-tagline">AI Powered ERP</p>
          </div>
        </div>
        <nav className="marketing-crm-nav-menu">
          {tabItems.map(tab => (
            <button key={tab.id} className={`marketing-crm-nav-item ${activeTab === tab.id ? 'marketing-crm-nav-item-active' : ''}`} onClick={() => setActiveTab(tab.id)}>
              <span className="marketing-crm-nav-icon">{tab.icon}</span>
              <span>{tab.label}</span>
            </button>
          ))}
        </nav>
        <div className="marketing-crm-logout-container">
          <button onClick={handleLogoutClick} className="marketing-crm-logout-button"><LogOut size={18} /> Logout</button>
        </div>
      </div>

      <div className="marketing-crm-main-content">
        <ScreenHeader
          title="Marketing & Research Dashboard"
          description={`Welcome, ${user?.name || user?.role || 'Marketing Manager'}`}
          timestamp={new Date().toLocaleString()}
          containerClassName="marketing-crm-header"
          titleClassName="marketing-crm-main-title"
          descriptionClassName="marketing-crm-subtitle"
          actionsClassName="marketing-crm-header-actions"
          timestampClassName="marketing-crm-last-updated"
        />

        {cameFromMDDashboard && (
          <div className="marketing-crm-back-button-wrapper">
            <button onClick={handleBackToMD} className="btn-edit" style={{ background: "linear-gradient(135deg, #4F46E5 0%, #6366F1 100%)" }}>
              <ArrowLeft size={18} /> Back to MD Dashboard
            </button>
          </div>
        )}

        <div className="marketing-crm-scrollable-content">
          <div className="marketing-crm-content-card">
            {renderContent()}
          </div>
        </div>

        {showLogoutModal && (
          <div className="logout-modal-overlay">
            <div className="logout-modal">
              <div className="logout-modal-header"><div className="logout-modal-icon">👋</div><h3>Confirm Logout</h3></div>
              <div className="logout-modal-body"><p>Are you sure you want to logout from <strong>Marketing & Research</strong> dashboard?</p><p className="logout-modal-warning">You will be redirected to the login page.</p></div>
              <div className="logout-modal-footer"><button className="logout-modal-cancel" onClick={handleCancelLogout}>Cancel</button><button className="logout-modal-confirm" onClick={handleConfirmLogout}>Yes, Logout</button></div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}